<script setup lang="ts">
import { helpers } from '@vuelidate/validators'
import { useVModel } from '@vueuse/core'
import type { FetchError } from 'ofetch'

import type { Contact, Contract } from '~/layers/forms/models'
import { Form } from '~/layers/forms/models'
import { emailValidator, requiredValidator } from '~/utils/validators'

import type { User } from '#auth-utils'

interface Props {
  modelValue: Pick<Contact, 'email'>
  processInfo: Contract
}

const { fetch, loggedIn, user, clear } = useUserSession()

const { cloned: auth, sync: resetAuth } = useCloned<
  Partial<{
    registered: boolean
    password: string
    confirmPassword: string
    requiredEmailValidation: boolean
    asGuest: boolean
  }>
>({
  requiredEmailValidation: !user.value?.emailConfirmed,
  registered: user.value?.id ? true : undefined,
})

const props = defineProps<Props>()
const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)
const toast = useToast()
const isLoading = ref(false)
const { $siteApi } = useNuxtApp()

const v$ = useValidation(
  () => {
    return auth.value.asGuest
      ? {}
      : {
          email: { required: requiredValidator, email: emailValidator },
          password: { required: requiredValidator },
          confirmPassword: {
            required: helpers.withMessage(
              'Confirme la contraseña.',
              () => auth.value.registered || !!auth.value.confirmPassword
            ),
            sameAs: helpers.withMessage(
              'La contraseña no coincide.',
              () => auth.value.registered || auth.value.password === auth.value.confirmPassword
            ),
          },
          authenticated: {
            required: helpers.withMessage(
              () => 'Es necesario completar la autenticación para continuar con tu solicitud',
              () => loggedIn.value && !!user.value?.emailConfirmed
            ),
          },
        }
  },
  computed(() => ({ ...model.value, ...auth.value })),
  {
    $scope: Form.auth,
    $registerAs: Form.auth,
  }
)

const reset = async () => {
  resetAuth()
  await clear()
}

const logIn = async () => {
  const email = model.value.email
  const password = auth.value.password
  if (!email || !password) return

  isLoading.value = true
  try {
    const email = model.value.email
    const password = auth.value.password
    const result = await $fetch<NuxtServerResponse<User, FetchError>>('/api/login', {
      method: 'post',
      body: { email, password },
    })
    if (result.error) {
      toast.add({
        severity: 'warn',
        summary: 'Autenticación',
        detail: 'Credenciales incorrectas',
        life: 5000,
      })
      isLoading.value = false
      return
    }

    await fetch()
    auth.value.requiredEmailValidation = !user.value?.emailConfirmed
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Autenticación',
      detail: 'Hubo un error, intentelo de nuevo más tarde, disculpe los inconvenientes.',
      life: 5000,
    })
  }
  isLoading.value = false
}

const signIn = async () => {
  const email = model.value.email
  const { password, confirmPassword } = auth.value
  if (!email || !password || password !== confirmPassword) return
  isLoading.value = true
  try {
    await $siteApi('/register', {
      method: 'post',
      body: {
        email: email,
        password: password,
        additionalData: {
          firstName: props.processInfo.customer.name,
          firstLastName: props.processInfo.customer.firstLastName,
          secondLastName: props.processInfo.customer.secondLastName,
          birthDate: props.processInfo.customer.birthDate?.toJSON() ?? undefined,
          gender: props.processInfo.biometrics.gender ?? undefined,
          mobile: props.processInfo.contact.phone,
          nationalId: props.processInfo.customer.identification.number,
        },
      },
    })

    await logIn()
    auth.value.requiredEmailValidation = true

    toast.add({
      severity: 'success',
      summary: 'Cuenta',
      detail: 'Tu cuenta fue creada.',
      life: 5000,
    })
  } catch (err) {
    const error = err as FetchError<SiteApiSchema<'ErrorResponse'>>
    const message = error?.data?.messages?.find((r) => r?.code === 'DuplicateEmail')
      ? 'Ya existe una cuenta con este correo electrónico'
      : 'Hubo un problema al intentar crear tu cuenta.'
    toast.add({
      severity: 'error',
      summary: 'Cuenta',
      detail: message,
      life: 5000,
    })
  }
  isLoading.value = false
}

const restorePassword = useDebounceFn(
  async () => {
    const email = model.value.email
    if (!email) {
      toast.add({
        severity: 'info',
        summary: 'Constraseña',
        detail: `Escribe tu correo electrónico para reestablecer tu contraseña.`,
        life: 5000,
      })

      return
    }
    isLoading.value = true
    try {
      await $siteApi('/forgotPassword', {
        method: 'post',
        body: { email },
      })

      toast.add({
        severity: 'success',
        summary: 'Constraseña',
        detail: `Se ha enviado a ${email} el correo de reestablecimiento de contraseña`,
        life: 5000,
      })
    } catch {
      toast.add({
        severity: 'error',
        summary: 'Constraseña',
        detail: 'Hubo un error, intentelo de nuevo más tarde, disculpe los inconvenientes.',
        life: 5000,
      })
    }

    isLoading.value = false
  },
  500,
  { maxWait: 60000 }
)

const submit = useDebounceFn(
  () => {
    if (auth.value.registered) logIn()
    else signIn()
  },
  500,
  { maxWait: 60000 }
)

const continueAsGuestHandler = (value: boolean) => {
  auth.value = {
    ...auth.value,
    asGuest: value,
    registered: undefined,
  }
}

watch(
  () => auth.value.registered,
  () => (auth.value.confirmPassword = undefined)
)

watch(
  () => auth.value.requiredEmailValidation,
  async (n, o) => {
    if (!n && o) await logIn()
  }
)
</script>

<template>
  <FormsGlobalContainer
    :identifier="Form.auth"
    :grid="false"
  >
    <FormsGlobalTitle
      text="Autenticación"
      icon-class="icon-[ph--lock-key-bold]"
    />

    <div class="flex flex-col items-center col-span-full">
      <PvMessage
        v-if="auth.asGuest"
        severity="info"
        class="flex flex-col justify-center w-full"
      >
        <div class="p-3 text-center">
          <div class="p-3 text-center flex flex-col gap-4">
            <div class="flex flex-col gap-2">
              <div class="flex items-center justify-center">
                <span class="icon-[ph--info-bold] text-4xl leading-none" />
              </div>
              <div class="text-xl font-bold leading-none">Estas contratando como invitado</div>
              <div class="text-xs font-normal">
                La información de esta poliza
                <span class="font-bold">no será vinculada a una cuenta</span>. <br />Se enviará el
                <span class="font-bold">certificado de emisión</span> de la misma al
                <span class="font-bold">correo electrónico</span> especificado en
                <span class="font-bold">información de contacto</span>.
              </div>
            </div>
            <PvButton
              class="text-lg"
              severity="contrast"
              style-as-input
              outlined
              @click="continueAsGuestHandler(false)"
            >
              Prefiero autenticarme
            </PvButton>
          </div>
        </div>
      </PvMessage>
      <CardComponent
        v-else
        bg="light"
        class="w-full md:max-w-md"
      >
        <template #header>
          <div class="py-1 md:py-4">
            <ImageComponent
              src="/img/logo-contigo.png"
              alt="Contigo"
              class="h-10 md:h-14"
            />
          </div>
        </template>
        <div class="flex flex-col gap-2 p-4">
          <template v-if="isLoading">
            <ProcessingComponent loading-message="Por favor espere..." />
          </template>
          <template v-else-if="auth.requiredEmailValidation && user?.id">
            <AuthEmailConfirmationForm
              v-model="auth.requiredEmailValidation"
              :user-id="user?.id"
              :email="model.email"
              :is-login="auth.registered"
            >
              <template #aditional-actions>
                <PvButton
                  text
                  style-as-input
                  @click="reset()"
                >
                  Intentar con otro correo
                </PvButton>
              </template>
            </AuthEmailConfirmationForm>
          </template>
          <template v-else-if="!loggedIn">
            <InputContainer :no-hint="auth.registered === undefined">
              <div> Ya tienes una cuenta? </div>
              <div class="flex flex-nowrap gap-4">
                <div class="flex gap-2 w-full">
                  <div class="flex items-center gap-2">
                    <PvRadioButton
                      v-model="auth.registered"
                      input-id="haveAccountTrueRadio"
                      name="haveAccount"
                      :value="true"
                    />
                    <label for="haveAccountTrueRadio">Si</label>
                  </div>
                  <div class="flex items-center gap-2">
                    <PvRadioButton
                      v-model="auth.registered"
                      input-id="haveAccountFalseRadio"
                      name="haveAccount"
                      :value="false"
                    />
                    <label for="haveAccountFalseRadio">No</label>
                  </div>
                </div>
              </div>
            </InputContainer>
            <template v-if="auth.registered !== undefined">
              <InputField
                label="Correo Electrónico"
                :error="v$.email?.$error"
                :error-message="v$.email?.$errors[0]?.$message.toString()"
              >
                <PvInputText v-model.lowercase="model.email" />
              </InputField>
              <InputField
                label="Contraseña"
                :error="v$.password?.$error"
                :error-message="v$.password?.$errors[0]?.$message.toString()"
              >
                <PvPassword
                  v-model="auth.password"
                  :feedback="!auth.registered"
                  fluid
                  toggle-mask
                />
              </InputField>
              <template v-if="!auth.registered">
                <InputField
                  label="Confirmar contraseña"
                  :error="v$.confirmPassword?.$error"
                  :error-message="v$.confirmPassword?.$errors[0]?.$message.toString()"
                >
                  <PvPassword
                    v-model="auth.confirmPassword"
                    :feedback="false"
                    fluid
                    toggle-mask
                  />
                </InputField>
              </template>
              <PvButton
                class="font-bold"
                style-as-input
                @click="submit()"
              >
                {{ auth.registered ? 'Iniciar Sesión' : 'Crear cuenta' }}
              </PvButton>
              <PvButton
                v-if="auth.registered"
                severity="secondary"
                style-as-input
                @click="restorePassword()"
              >
                Olvide la contraseña
              </PvButton>
            </template>
            <PvButton
              severity="contrast"
              outlined
              style-as-input
              @click="continueAsGuestHandler(true)"
            >
              Continuar como invitado
            </PvButton>
          </template>

          <template v-else>
            <div class="py-5 text-center">
              <div class="text-xl font-bold uppercase">Gracias por autenticarte</div>
              <div>Puedes continuar con tu solicitud</div>
            </div>
          </template>
        </div>
      </CardComponent>
      <div
        class="col-span-full text-center"
        :class="v$.authenticated?.$errors?.[0]?.$message ? 'p-invalid' : ''"
      >
        <span class="text-negative">
          {{ v$.authenticated?.$errors?.[0]?.$message }}
        </span>
      </div>
    </div>
  </FormsGlobalContainer>
</template>
